import React, { useEffect, useRef, useState } from 'react'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Button, Input, InputRef, Space, Table, Tag } from 'antd'
import { useTradersList } from './hooks'
import { ColumnsType } from 'antd/lib/table'
import { DataIndex, PartnerItem } from './types'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { BanModal } from '../../atoms/banModal/component'

export const PartnersList = () => {
  const { partners, loading, totalCountOfPartners, onChangePartnersList } =
    useTradersList()

  const [openModalProps, setOpenModalProps] = useState({
    isOpen: false,
    id: '',
    type: '',
  })

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('rating')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }
  }

  useEffect(() => {
    onChangePartnersList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText])

  const searchInput = useRef<InputRef>(null)

  const handleOpenModal = (id: number, type: string) => {
    setOpenModalProps({
      isOpen: true,
      id: String(id),
      type,
    })
  }

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return {
        ...prev,
      }
    })
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<PartnerItem> = [
    {
      title: 'Rating',
      dataIndex: 'rating',
      sorter: (a, b) => a.rating - b.rating,
      defaultSortOrder: 'descend',
    },
    {
      title: 'ID',
      dataIndex: 'clientId',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      ...getColumnSearchProps('email'),
    },
    {
      title: 'Points',
      dataIndex: 'points',
      sorter: (a, b) => a.points - b.points,
      defaultSortOrder: 'descend',
    },
    {
      title: 'All clients',
      dataIndex: 'activeClients',
      sorter: (a, b) => a.activeClients - b.activeClients,
      defaultSortOrder: 'descend',
    },
    {
      title: 'New clients',
      dataIndex: 'newClients',
      sorter: (a, b) => a.newClients - b.newClients,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Lots traded',
      dataIndex: 'lots',
      sorter: (a, b) => Number(a.lots) - Number(b.lots),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Cash',
      dataIndex: 'cash',
      sorter: (a, b) => Number(a.cash) - Number(b.cash),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Deposit',
      dataIndex: 'deposits',
      sorter: (a, b) => Number(a.deposits) - Number(b.deposits),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Withdrawal',
      dataIndex: 'withdrawals',
      sorter: (a, b) => Number(a.withdrawals) - Number(b.withdrawals),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item) => {
        return (
          <div className="d-flex flex-column gap-1">
            <CButton
              color="danger"
              onClick={() => handleOpenModal(item.clientId, 'partner')}
            >
              Ban
            </CButton>
            <CButton color="success">Cash prize</CButton>
          </div>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <Table
        dataSource={partners}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="partnersId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfPartners,
        }}
        onChange={(pagination, filters, sorter) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
          })
        }
      ></Table>
      <BanModal
        id={openModalProps.id}
        typeOfClient={openModalProps.type}
        isVisible={openModalProps.isOpen}
        onClose={() =>
          setOpenModalProps((prev) => ({ ...prev, isOpen: false }))
        }
      />
    </CCardBody>
  )
}
